import moment from 'moment';
import { downloadText } from '../../../common/utils';

const CSV_HEADER = 'mission,date';

const ACCEPTED_DATE_FORMATS = [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'];

const toDateStr = (d) => (typeof d === 'string' ? d : (d?.toISOString?.() ?? String(d)));


const getImageDay = (img) => moment(img?.date).format('YYYY-MM-DD');

const getImageMission = (img) => img?.shortname ?? img?.name ?? '';

/**
 * @function getImageKey
 * @description Builds the unique selection key of an image: its id if present, otherwise a
 * date/name based fallback. Mirrors ImageTable's own key logic so that every screen rendering
 * the table agrees on which key identifies which row.
 * @param {Object} img - Image object, as produced by the acquisition reducers.
 * **Called by:
 * @see {ImageListRefiner} - shoreline/shoreR, mepbay and intertidal variants
 * @see {ShoreSImageSelector}
 * @see {buildSelectionFromCsv}
 * @returns {String} Unique key identifying the image.
 */
export const getImageKey = (img) =>
  img?.id ?? `${toDateStr(img?.date)}-${img?.name ?? img?.shortname ?? ''}`;

/**
 * @function getImageCsvKeys
 * @description Every key an image may be referenced by in a CSV: `day-shortname`, plus
 * `day-fullMissionName` so files exported before the format was shortened still match.
 * @param {Object} img - Image object.
 * **Called by:
 * @see {buildSelectionFromCsv}
 * @returns {String[]} Accepted keys, deduplicated.
 */
export const getImageCsvKeys = (img) => {
  const day = getImageDay(img);
  return [...new Set([`${day}-${getImageMission(img)}`, `${day}-${img?.name ?? ''}`])];
};

/**
 * @function parseImageDatesCsv
 * @description Parses the date CSV exported by the image chooser cards (header "mission,date",
 * one row per image). The header line is optional and any extra column is ignored. Dates are
 * normalized to their calendar day, so a file carrying full timestamps still matches.
 * @param {String} text - Raw file contents.
 * **Called by:
 * @see {buildSelectionFromCsv}
 * @returns {Array<{mission: String, day: String}>} One entry per valid data row.
 */
export const parseImageDatesCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  // Accepts the current "mission,date" header and the "name,date" one used before.
  const hasHeader = /^(mission|name)\s*,\s*date/i.test(lines[0] ?? '');
  const dataLines = hasHeader ? lines.slice(1) : lines;

  return dataLines
    .map((line) => {
      const [mission, date] = line.split(',');
      const parsed = moment(date?.trim(), ACCEPTED_DATE_FORMATS);
      return { mission: mission?.trim(), day: parsed.isValid() ? parsed.format('YYYY-MM-DD') : null };
    })
    .filter((row) => row.mission && row.day);
};

/**
 * @function buildSelectionFromCsv
 * @description Turns a date CSV into a complete selection map for the given image list. The CSV
 * *replaces* the current selection: images listed in it are checked and every other image is
 * unchecked, so importing always yields exactly what the file describes.
 * @param {String} text - Raw file contents.
 * @param {Array<Object>} images - Images currently displayed by the table.
 * **Called by:
 * @see {ImportDatesButton}
 * @returns {{selection: Object, matched: Number, total: Number, unmatched: Array<String>}}
 * The selection map plus counters for user feedback.
 */
export const buildSelectionFromCsv = (text, images = []) => {
  const rows = parseImageDatesCsv(text);
  const importedKeys = new Set(rows.map(({ mission, day }) => `${day}-${mission}`));

  const selection = {};
  const matchedKeys = new Set();

  images.forEach((img) => {
    const hits = getImageCsvKeys(img).filter((key) => importedKeys.has(key));
    selection[getImageKey(img)] = hits.length > 0;
    hits.forEach((key) => matchedKeys.add(key));
  });

  const unmatched = [...importedKeys].filter((key) => !matchedKeys.has(key));

  return {
    selection,
    matched: importedKeys.size - unmatched.length,
    total: importedKeys.size,
    unmatched,
  };
};

/**
 * @function exportImagesAsCsv
 * @description Downloads an image list as the date CSV that `parseImageDatesCsv` reads back,
 * so a selection exported from any vertical round-trips into its image selection step.
 * @param {Array<Object>} images - Images to write, one per row.
 * @param {String} fileName - Name of the downloaded file.
 * **Called by:
 * @see {ImageChooserCard} - map and mepbay variants
 * @returns {void}
 */
export const exportImagesAsCsv = (images = [], fileName) => {
  const rows = new Map();
  images.forEach((img) => {
    const mission = getImageMission(img);
    const day = getImageDay(img);

    rows.set(`${day}-${mission}`, `${mission},${day}`);
  });
  downloadText([CSV_HEADER, ...rows.values()].join('\n'), fileName, 'text/csv');
};
